import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/utils/toast";
import { getApiUrl } from "@/utils/api";
import { useTranslation } from "react-i18next";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Category } from "@/types";
import { Badge } from "@/components/ui/badge";

interface CompanyCategoriesFormProps {
  companyId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface CompanyCategory {
  id: number;
  company: number;
  category: number;
  category_details: {
    id: number;
    name: string;
  };
  is_active: boolean;
}

const categorySchema = z.object({
  categories: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      is_active: z.boolean().default(false),
    }),
  ),
});

type CategoryFormValues = z.infer<typeof categorySchema>;

export default function CategoriesForm({
  companyId,
  onSuccess,
  onCancel,
}: CompanyCategoriesFormProps) {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Array<Category>>([]);
  const [userCompanyId, setUserCompanyId] = useState<number | null>(null);

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      categories: [],
    },
  });

  // Fetch all categories
  const { data: allCategories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("categories/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();

      // Ensure we return an array
      if (data && data.results && Array.isArray(data.results)) {
        return data.results;
      } else if (Array.isArray(data)) {
        return data;
      } else {
        console.error("Unexpected categories data format:", data);
        return [];
      }
    },
  });

  // Fetch company categories
  const {
    data: companyCategories,
    isLoading: isLoadingCompanyCategories,
    isError,
    error,
  } = useQuery({
    queryKey: ["company-categories", companyId],
    queryFn: async () => {
      if (!companyId) {
        return [];
      }
      try {
        const url = getApiUrl(
          `company-categories/by_company/?company_id=${companyId}`,
        );
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to fetch company categories: ${response.status} ${errorText}`,
          );
        }

        const data = await response.json();

        // Ensure we return an array
        if (data && data.results && Array.isArray(data.results)) {
          return data.results;
        } else if (Array.isArray(data)) {
          return data;
        } else {
          console.error("Unexpected company categories data format:", data);
          return [];
        }
      } catch (err) {
        throw err;
      }
    },
    enabled: !!companyId,
  });

  // Handle error from company categories query
  useEffect(() => {
    if (isError && error) {
      console.error("Query error:", error);
      toast.error(`Failed to fetch company categories: ${error.message}`);
    }
  }, [isError, error]);

  // Process categories when both queries are complete
  useEffect(() => {
    if (allCategories && companyCategories) {
      // Ensure allCategories is an array before mapping
      if (!Array.isArray(allCategories)) {
        console.error("allCategories is not an array:", allCategories);
        return;
      }

      const categoriesWithStatus = allCategories.map((category: Category) => {
        // Make sure companyCategories is an array before using find
        const companyCategory = Array.isArray(companyCategories)
          ? companyCategories.find(
              (cc: CompanyCategory) => cc.category === category.id,
            )
          : undefined;

        return {
          id: category.id,
          name: category.name,
          level: category.level,
          is_active: companyCategory ? companyCategory.is_active : false,
        };
      });

      setCategories(categoriesWithStatus);
      form.reset({
        categories: categoriesWithStatus,
      });
    }
  }, [allCategories, companyCategories, form]);

  // Mutation to update company categories
  const mutation = useMutation({
    mutationFn: async (values: CategoryFormValues) => {
      if (!companyId) throw new Error("Company ID is required");

      // Create or update company categories
      const updatePromises = values.categories.map(async (category) => {
        // Check if the company-category relation already exists
        const existingCompanyCategory = Array.isArray(companyCategories)
          ? companyCategories.find(
              (cc: CompanyCategory) => cc.category === category.id,
            )
          : undefined;

        const url = existingCompanyCategory
          ? getApiUrl(`company-categories/${existingCompanyCategory.id}/`)
          : getApiUrl("company-categories/");

        const method = existingCompanyCategory ? "PATCH" : "POST";

        const body = existingCompanyCategory
          ? { is_active: category.is_active }
          : {
              company: companyId,
              category: category.id,
              is_active: category.is_active,
            };

        const response = await fetch(url, {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorData = await response.text();
          console.error(`Error response from ${url}:`, errorData);
          throw new Error(`Failed to update category: ${errorData}`);
        }

        const responseData = await response.json();
        return responseData;
      });

      return Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["company-categories", companyId],
      });

      // Show success toast
      toast.success("Categories updated successfully");
      onSuccess();
    },
    onError: (error: Error) => {
      console.error("Error updating categories:", error);

      // Show error toast
      toast.error(error.message);
    },
  });

  const onSubmit = (values: CategoryFormValues) => {
    mutation.mutate(values);
  };

  if (isLoadingCompanyCategories || (!companyId && !userCompanyId)) {
    return (
      <div className="flex justify-center items-center h-64">Loading...</div>
    );
  }

  const handleToggleCategory = (index: number, checked: boolean) => {
    const updatedCategories = [...categories];
    updatedCategories[index].is_active = checked;
    setCategories(updatedCategories);
    form.setValue("categories", updatedCategories);
  };

  // Render the component
  return (
    <Form {...form}>
      {/* <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold">
            {t("common.my-categories")}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {t("common.select-categories")}
          </p>
        </div>
      </div> */}

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full pb-24 overflow-y-auto"
      >
        {categories.length === 0 ? (
          <div className="flex justify-center items-center h-64 text-gray-500">
            No categories available
          </div>
        ) : (
          <div className="space-y-6 overflow-y-auto flex-1 px-4">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className="flex items-center justify-between py-4 border-b border-gray-200"
              >
                <div className="w-full flex items-center justify-start gap-4">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                      {category.name.charAt(0)}
                    </div>
                  </div>
                  <div className="w-full flex items-center justify-between pr-8 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {t(category.name)}
                      </h3>
                    </div>
                    <div>
                      <Badge>
                        <h3 className="text-sm text-gray-900">
                          {t("common.level")} {category.level}
                        </h3>
                      </Badge>
                    </div>
                  </div>
                </div>
                <div>
                  <Switch
                    checked={category.is_active}
                    onCheckedChange={(checked) =>
                      handleToggleCategory(index, checked)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Fixed Save button at the bottom */}
        <div className="fixed bottom-0 left-0 right-0 py-4 px-4 bg-white border-t border-gray-200 shadow-md">
          <div className="flex justify-end">
            <div>
              <Button
                variant="gradient"
                type="submit"
                className="w-full max-w-md bg-primary hover:bg-accent text-white font-semibold py-3 rounded-lg shadow-md"
                disabled={mutation.isPending || categories.length === 0}
              >
                {mutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
